// MultiSelectBusca — input com busca + dropdown + tags removíveis.
// Padrão canônico do parque (portal-rh). Ver centrao-docs/02 § "Multi-select com busca".
//
// Props: options [{ value, label, hint? }], values [], onChange(newValues),
//        placeholder, icon (bi class), cor { tagBg, tagFg, tagBorder, focusBorder }, disabled
import React, { useEffect, useMemo, useRef, useState } from 'react';

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export default function MultiSelectBusca({
  options = [],
  values = [],
  onChange,
  placeholder = 'Buscar...',
  icon,
  cor = { tagBg: '#e7f1ea', tagFg: '#1f6a2d', tagBorder: '#bcd9c6', focusBorder: '#1f6a2d' },
  disabled = false,
}) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const valSet = useMemo(() => new Set(values.map((v) => String(v))), [values]);

  const filtradas = useMemo(() => {
    const q = norm(input);
    return options
      .filter((o) => !valSet.has(String(o.value)))
      .filter((o) => {
        if (!q) return true;
        return norm(`${o.label} ${o.hint || ''} ${o.value}`).includes(q);
      })
      .slice(0, 100);
  }, [options, valSet, input]);

  // Click fora fecha + limpa (mousedown, não click — ver centrao-docs/06)
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false); setInput(''); setFocusIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const add = (opt) => {
    onChange([...values, opt.value]);
    setFocusIdx(-1);
    setOpen(true);
    inputRef.current?.focus();
  };
  const remove = (val) => onChange(values.filter((v) => String(v) !== String(val)));

  const onKey = (e) => {
    if (e.key === 'Escape') { setOpen(false); setInput(''); setFocusIdx(-1); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setFocusIdx((i) => Math.min(i + 1, filtradas.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && focusIdx >= 0 && filtradas[focusIdx]) { e.preventDefault(); add(filtradas[focusIdx]); }
    else if (e.key === 'Backspace' && !input && values.length > 0) { remove(values[values.length - 1]); }
  };

  useEffect(() => {
    if (focusIdx < 0 || !listRef.current) return;
    const node = listRef.current.children[focusIdx];
    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [focusIdx]);

  const isOpen = open && !disabled;
  const labelDe = (val) => options.find((o) => String(o.value) === String(val))?.label || String(val);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        onClick={() => { if (!disabled) { setOpen(true); inputRef.current?.focus(); } }}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center',
          padding: '5px 8px', minHeight: 38,
          border: `1px solid ${isOpen ? cor.focusBorder : '#ced4da'}`, borderRadius: 8,
          background: disabled ? '#e9ecef' : '#fff', cursor: disabled ? 'not-allowed' : 'text', fontSize: '0.85rem',
        }}>
        {icon && values.length === 0 && !input && (
          <i className={`bi ${icon}`} style={{ color: '#9CA3AF', fontSize: '0.85rem' }} />
        )}
        {values.map((v) => (
          <span key={v} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: cor.tagBg, color: cor.tagFg, border: `1px solid ${cor.tagBorder}`,
            borderRadius: 6, padding: '2px 6px', fontSize: '0.78rem', fontWeight: 600,
          }}>
            {labelDe(v)}
            {!disabled && (
              <button onClick={(e) => { e.stopPropagation(); remove(v); }}
                style={{ border: 'none', background: 'transparent', color: cor.tagFg, padding: 0, lineHeight: 1, cursor: 'pointer' }}>
                <i className="bi bi-x-lg" style={{ fontSize: '0.7rem' }} />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef} value={input}
          onChange={(e) => { setInput(e.target.value); setOpen(true); setFocusIdx(-1); }}
          onFocus={() => setOpen(true)} onKeyDown={onKey}
          placeholder={values.length === 0 ? placeholder : ''} disabled={disabled}
          style={{ flex: 1, minWidth: 100, border: 'none', outline: 'none', fontSize: '0.85rem', background: 'transparent' }}
        />
      </div>

      {isOpen && (
        <div ref={listRef} style={{
          position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 260, overflowY: 'auto',
          background: '#fff', border: '1px solid #ddd', borderRadius: 8, marginTop: 2, zIndex: 50,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          {filtradas.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#888', textAlign: 'center' }}>
              {input ? 'Nenhum resultado' : (options.length === valSet.size ? 'Todos já selecionados' : 'Sem opções')}
            </div>
          ) : filtradas.map((o, i) => (
            <div key={o.value}
              onMouseDown={(e) => e.preventDefault()} onClick={() => add(o)} onMouseEnter={() => setFocusIdx(i)}
              style={{
                padding: '7px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                background: i === focusIdx ? '#eef6f0' : '#fff', fontSize: '0.83rem',
              }}>
              <strong>{o.label}</strong>
              {o.hint && <span style={{ color: '#6B7280', marginLeft: 6, fontSize: '0.75rem' }}>{o.hint}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
